import { useEffect, useState } from 'react';

import { useLocation } from 'react-router';

import {
  getStickyVerifyCustomerId,
  getVerifiedCustomerId,
  setStickyVerifyCustomerId,
  setVerifiedCustomerId,
} from '~/utils/verificationCookies';

export const useStickyVerifyCustomer = () => {
  const location = useLocation();
  const [customerId, setCustomerId] = useState<string | null>(null);
  const [isVerified, setIsVerified] = useState(false);

  // Check URL parameters and cookies on mount and location change
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const urlCustomerId = urlParams.get('stickyVerifyCustomer');
    const verifiedParam = urlParams.get('verified');
    const cookieCustomerId = getStickyVerifyCustomerId();
    const verifiedCookieCustomerId = getVerifiedCustomerId();

    // URL parameters always take precedence over cookies
    if (urlCustomerId) {
      // If both params are in URL, set customerVerified cookie
      if (verifiedParam === 'true') {
        setCustomerId(urlCustomerId);
        setIsVerified(true);
        setVerifiedCustomerId(urlCustomerId);
        // Clear stickyVerifyCustomer cookie if it exists
        setStickyVerifyCustomerId(null);
        return;
      }

      // Check if URL customer matches verified cookie
      if (verifiedCookieCustomerId && verifiedCookieCustomerId === urlCustomerId) {
        // URL matches verified cookie - use verified state
        setCustomerId(urlCustomerId);
        setIsVerified(true);
        return;
      }

      // URL customer doesn't match verified cookie (or no verified cookie) - use URL, clear verified cookie
      setCustomerId(urlCustomerId);
      setIsVerified(false);
      setStickyVerifyCustomerId(urlCustomerId);
      // Clear verified cookie if switching to different customer
      if (verifiedCookieCustomerId && verifiedCookieCustomerId !== urlCustomerId) {
        setVerifiedCustomerId(null);
      }
      return;
    }

    // No URL parameter - only show banner if there's a pending verification (stickyVerifyCustomer cookie)
    // Don't show banner just because customerVerified cookie exists - that's for background checks only
    if (cookieCustomerId) {
      // Check if this pending customer is already verified
      if (verifiedCookieCustomerId && verifiedCookieCustomerId === cookieCustomerId) {
        setCustomerId(cookieCustomerId);
        setIsVerified(true);
      } else {
        setCustomerId(cookieCustomerId);
        setIsVerified(false);
      }
    } else {
      // No pending verification - don't show banner
      // (customerVerified cookie may still exist for isCustomerVerified() checks)
      setCustomerId(null);
      setIsVerified(false);
    }
  }, [location.search]);

  const closeStickyVerify = () => {
    setCustomerId(null);
    setIsVerified(false);
    // Only clear the pending verification cookie, keep the verified cookie active
    setStickyVerifyCustomerId(null);
  };

  const markAsVerified = () => {
    setIsVerified(true);
    if (customerId) {
      setVerifiedCustomerId(customerId);
    }
    setStickyVerifyCustomerId(null);
  };

  return {
    customerId,
    isVerified,
    closeStickyVerify,
    markAsVerified,
  };
};
